import { Directive, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { GooglePay } from '@df/module-google-pay/google-pay.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { AbstractAdyen3dsComponent } from '../abstract-adyen-3ds.component';
import { Adyen } from '../adyen.namespace';

@Directive()
export abstract class AdyenGooglePayComponentDef extends AbstractAdyen3dsComponent<
    Adyen.IGooglePayPaymentMethod,
    Adyen.IGooglePayComponentOptions
> {
    protected readonly methodCode = Adyen.PaymentMethod.GooglePay;

    /**
     * Selector for component container
     * @type {string}
     */
    protected readonly containerSelector = '#adyen-google-pay-container';

    /**
     * Adyen checkout component id
     * @type  {string}
     */
    protected readonly componentId = Adyen.Component.GooglePay;

    /**
     * Adyen checkout component instance
     */
    protected component?: Adyen.IGooglePayComponent;

    googlePayAvailableStatus = new Status();

    /**
     * Flag if component was already initiated
     * @type {boolean}
     */
    private componentInitiated = false;

    private lastTotal?: number;


    private timeoutService = inject(TimeoutService);

    private initComponentDebounced = this.timeoutService.debounce(() => this.initComponent(), 200);

    /**
     * Initiates adyen component
     */
    protected initComponent(): void {
        if (!this.componentInitiated) {
            this.componentInitiated = true;
            this.subscriptions.add(
                this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => {
                    if (this.getTotal() !== this.lastTotal) {
                        this.initComponentDebounced();
                    }
                })
            );
            this.subscriptions.add(this.googlePayAvailableStatus.subscribe(() => this.detectChanges()));
            this.subscriptions.add(this.checkoutStepComponent.validityChangeSubject.subscribe(() => this.detectChanges()));
        }
        super.initComponent();
    }

    /**
     * Returns adyen google pay component configuration
     * @returns {Adyen.IGooglePayComponentOptions}
     */
    protected getComponentOptions(): Adyen.IGooglePayComponentOptions | undefined {
        const amount = this.getTotal();
        const { merchantAccount, merchantIdentifier } = this.method;
        this.lastTotal = amount;
        if (amount && (merchantIdentifier || this.method?.isTest)) {
            return {
                showButton: true,
                environment: this.method.checkoutEnvironment?.toUpperCase() || 'TEST',
                configuration: {
                    merchantIdentifier,
                    merchantName: merchantAccount,
                    gatewayMerchantId: merchantAccount
                },
                amount,
                currency: this.app.data.currency.base,
                totalPriceStatus: 'FINAL',
                onAuthorized: (event: GooglePay.IPaymentData) => {
                    if (this.isCheckoutValid) {
                        const submissionData = this.checkoutFormComponent.getSubmissionData();
                        submissionData.paymentMethod = {
                            method: this.methodCode,
                            additional_data: {
                                token: event?.paymentMethodData?.tokenizationData?.token
                            }
                        };
                        this.checkoutFormComponent.submitWithData(submissionData);
                    } else {
                        this.validateCheckout();
                    }
                },
                buttonColor: 'black',
                buttonType: 'long',
                existingPaymentMethodRequired: false
            };
        }
        return undefined;
    }

    /**
     * Mounts adyen component in container
     * @param {Adyen.IGooglePayComponent} adyenComponent
     */
    protected mount(adyenComponent: Adyen.IGooglePayComponent): void {
        adyenComponent
            .isAvailable()
            .then(() => {
                // we have to 'unmount' again because of 'isAvailable' promise, other instance could be mounted in meantime
                this.onAvailabilityUpdate(true);
                this.unmount();
                this.component = adyenComponent;
                this.googlePayAvailableStatus.success = 'SUCCESS';
                super.mount(adyenComponent);
            })
            .catch(() => {
                this.googlePayAvailableStatus.error = 'Google Pay is not available. Please select another payment method.';
                this.onAvailabilityUpdate(false);
            });
    }

    /**
     * Override, this method use specific submit button/flow
     */
    protected onCheckoutSubmit(): void {}

    /**
     * Check if all steps in checkout are valid.
     * Used to show additional last step or content if entire checkout is valid.
     * (e.g on OB show 'Review your order' section)
     */
    get isCheckoutValid(): boolean {
        return this.checkoutFormComponent.isCheckoutValid;
    }

    /**
     * Validate checkout (e.g on submit) and scroll to first error if any.
     */
    validateCheckout(): void {
        this.checkoutFormComponent.validateCheckout();
    }

    /**
     * Converts amount to match adyen converter
     * @param {number} value
     * @returns {number}
     */
    private getAmount(value: number, format = 2): number {
        return Math.round(value * Math.pow(10, format));
    }

    /**
     * Returns total amount to pay
     * @returns {number | undefined}
     */
    private getTotal(): number | undefined {
        const total = this.app.customer.basket.data?.totals?.grand_total;
        if (total !== undefined) {
            return this.getAmount(total, this.method?.format);
        }
        return total;
    }

    protected getJsAssets(): string[] {
        return [...super.getJsAssets(), GooglePay.Scripts.pay];
    }

    /**
     * Sets google pay button component availability flag
     * @param {boolean} to
     */
    private onAvailabilityUpdate(to: boolean): void {
        this.adyen.isGooglePayAvailable = to;
    }
}